import ProcessService from 'services/ProcessService';
import DodService from 'services/DodService';
import { history } from 'utils/History';

/**
 * Utility class used to navigate to different routes.
 */
class NavigationUtils {
    constructor() {
        this.clientBasePath = process.env.REACT_APP_CLIENT_BASE_PATH;
    }

    getHistory() {
        return history;
    }

    getNavClickFunction(processId, revisionNumber, relativePath) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNumber, relativePath));
        };
    }

    getPath(processId, revisionNumber, relativePath) {
        let basePath = `/process/${processId}`;
        if (revisionNumber) {
            basePath += `/revision/${revisionNumber}`;
        }
        if (relativePath) {
            basePath += relativePath;
        }
        return basePath;
    }

    removeTrailingSlash(path) {
        return path.replace(/\/+$/, ''); // remove trailing slash
    }

    /**
     * parse the path into an array of segments
     * @param path
     * @returns {*}
     */
    getPathSegments(path) {
        path = this.removeTrailingSlash(path);
        const segments = path.split('/');
        for (let i = segments.length - 1; i >= 0; i--) {
            if (!segments[i]) {
                // get rid of empty segments
                segments.splice(i, 1);
            }
        }
        return segments;
    }

    /**
     * Used to open PCM Process path in new window, transferring state via local storage so the page doesn't have
     * to re-load all the data.  Do not use this method for external links.
     * @param processId
     * @param relativePath
     */
    openInNewWindow(processId, relativePath) {
        // Save the current state to local storage so it can be transferred to the new window
        const processData = ProcessService.getMyState(ProcessService.getStore().getState()).originalCopiesById[processId];
        const dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
        const prodDods = DodService.getMyState(DodService.getStore().getState()).prodDods;

        localStorage.setItem(processId, JSON.stringify(processData));
        localStorage.setItem('dodsById', JSON.stringify(dodsById));
        localStorage.setItem('prodDods', JSON.stringify(prodDods));

        window.open(relativePath, '_blank', 'toolbar=0,location=0,menubar=0,height=600,width=800');
    }


    /**
     * Get a callback function used to open an external link in a new window.  On Chrome, it checks if a tab with
     * that url is already open, and just focuses that tab instead to avoid polluting the browser with lots of tabs
     * open to the same page.  Note this only works in Chrome, not FF.
     * @param url
     * @returns {Function}
     */
    openExternalLinkInNewWindowFunc = (url) => {
        return () => {
            // Stupid FF doesn't support this
            const isFirefox = typeof InstallTrigger !== 'undefined';
            if (isFirefox) {
                window.open(url, '_blank');

            } else {
                // Only open in a new tab if one is not already open, otherwise just focus the open one
                let winref = window.open('', url);

                if (winref.location.href === 'about:blank') {
                    window.open(url, url).focus();
                } else {
                    winref.focus();
                }
            }
        };
    };

    navigateToPath(relativePath, processId, newWindow) {
        if (newWindow) {
            this.openInNewWindow(processId, relativePath);

        } else {
            setTimeout(() => {
                this.getHistory().push(relativePath);
            }, 100);
        }
    }

    gotoSearch = (newWindow) => {
        this.navigateToPath('/search', null, newWindow);
    };

    gotoSearchFunc(newWindow) {
        return () => {
            this.gotoSearch(newWindow);
        };
    }

    gotoPcmHome = (newWindow) => {
        this.navigateToPath('/', null, newWindow);
    };

    gotoPcmHomeFunc(newWindow) {
        return () => {
            this.gotoPcmHome(newWindow);
        };
    }

    gotoHome = (newWindow) => {
        this.navigateToPath('/processes', null, newWindow);
    };

    gotoHomeFunc(newWindow) {
        return () => {
            this.gotoHome(newWindow);
        };
    }

    gotoTutorials = (newWindow) => {
        this.navigateToPath('/processes/tutorials', null, newWindow);
    };

    gotoTutorialsFunc(newWindow) {
        return () => {
            this.gotoTutorials(newWindow);
        };
    }

    gotoProcess(processId, revisionNum, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum), processId, newWindow);
    }

    gotoProcessFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum));
        };
    }

    gotoProcessRevisionHistory(processId, revisionNum, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, '/revision-history'), processId, newWindow);
    }

    gotoProcessRevisionHistoryFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum, '/revision-history'));
        };
    }

    gotoDependencyTree(id, newWindow) {
        if (newWindow) {
            const processData = ProcessService.getMyState(ProcessService.getStore().getState()).processesById;
            const dodsById = DodService.getMyState(DodService.getStore().getState()).dodsById;
            const prodDods = DodService.getMyState(DodService.getStore().getState()).prodDods;

            localStorage.setItem('processesById', JSON.stringify(processData));
            localStorage.setItem('dodsById', JSON.stringify(dodsById));
            localStorage.setItem('prodDods', JSON.stringify(prodDods));

            //local environment value is just slash, don't include slash then if that's the case
            const windowName = `${this.clientBasePath}${this.clientBasePath !== '/' ? '/' : ''}dependencies`;
            const dependencyUrl = `${windowName}/${id}`;

            const isFirefox = typeof InstallTrigger !== 'undefined';
            if (isFirefox) {
                window.open(dependencyUrl, '_blank');

            } else {
                // Only open in a new tab if one is not already open, otherwise just focus the open one
                let winref = window.open(dependencyUrl, windowName);

                if (winref.location.href === 'about:blank') {
                    window.open(dependencyUrl, windowName).focus();
                } else {
                    winref.focus();
                }
            }
        } else {
            const relativePath = `/dependencies/${id}`;
            setTimeout(() => {
                this.getHistory().push(relativePath);
            }, 100);
        }
    }

    gotoVariableRetrieval(processId, revisionNum, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, '/variable-retrieval'), processId, newWindow);
    }

    gotoVariableRetrievalFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum, '/variable-retrieval'));
        };
    }

    gotoVariableRetrievalSpreadsheet(processId, revisionNum, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, '/variable-retrieval/spreadsheet'), processId, newWindow);
    }

    gotoVariableRetrievalSpreadsheetFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum, '/variable-retrieval/spreadsheet'));
        };
    }

    gotoRetrievalRules(processId, revisionNum, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, '/variable-retrieval/retrieval-rules'), processId, newWindow);
    }

    gotoRetrievalRulesFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum, '/variable-retrieval/retrieval-rules'));
        };
    }

    gotoInputDataset(processId, revisionNum, inputDatasetId, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, `/variable-retrieval/input-dataset/${inputDatasetId}`), processId, newWindow);
    }

    gotoInputDatasetFunc(processId, revisionNum, inputDatasetId) {
        if (inputDatasetId) {
            return () => {
                this.getHistory().push(this.getPath(processId, revisionNum, `/variable-retrieval/input-dataset/${inputDatasetId}`));
            };
        } else {
            return null;
        }
    }

    gotoRetrievedVariable(processId, revisionNum, retrievedVariableId, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, `/variable-retrieval/retrieved-variable/${retrievedVariableId}`), processId, newWindow);
    }

    gotoRetrievedVariableFunc(processId, revisionNum, retrievedVariableId) {
        if (retrievedVariableId) {
            return () => {
                this.getHistory().push(this.getPath(processId, revisionNum, `/variable-retrieval/retrieved-variable/${retrievedVariableId}`));
            };
        } else {
            return null;
        }
    }

    gotoCoordinateSystem(processId, revisionNum, coordinateSystemId, dimensionName, newWindow) {

        // If there is no dimension specified, then use the first dimension defined for this coordinate system
        if (!dimensionName) {
            let process = null;
            if (revisionNum) {
                process = ProcessService.getMyState(ProcessService.getStore().getState()).processRevisionsById[processId][revisionNum];
            } else {
                process = ProcessService.getMyState(ProcessService.getStore().getState()).workingCopiesById[processId];
            }
            dimensionName = Object.keys(process.variable_retrieval.coordinate_systems[coordinateSystemId].dimensions)[0];
        }
        this.navigateToPath(this.getPath(processId, revisionNum, `/variable-retrieval/coordinate-system/${coordinateSystemId}/${dimensionName}`), processId, newWindow);
    }

    gotoCoordinateSystemFunc(processId, revisionNum, coordinateSystemId, dimensionName) {
        // TODO: check all usages
        if (coordinateSystemId) {
            return () => {
                this.getHistory().push(this.getPath(processId, revisionNum, `/variable-retrieval/coordinate-system/${coordinateSystemId}/${dimensionName}`));
            };
        } else {
            return null;
        }
    }

    gotoOutputDatastream(processId, revisionNum, outputDatastreamId, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, `/variable-retrieval/output-datastream/${outputDatastreamId}`), processId, newWindow);
    }

    gotoOutputDatastreamFunc(processId, revisionNum, outputDatastreamId) {
        if (outputDatastreamId) {
            return () => {
                this.getHistory().push(this.getPath(processId, revisionNum, `/variable-retrieval/output-datastream/${outputDatastreamId}`));
            };
        } else {
            return null;
        }
    }

    gotoAdvancedTransformation(processId, revisionNum, coordinateSystemName, dimensionName, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, `/variable-retrieval/transformations/${coordinateSystemName}/${dimensionName}`), processId, newWindow);
    }

    gotoAdvancedTransformationFunc(processId, revisionNum, coordinateSystemName, dimensionName) {
        if (coordinateSystemName & dimensionName) {
            return () => {
                this.getHistory().push(this.getPath(processId, revisionNum, `/variable-retrieval/transformations/${coordinateSystemName}/${dimensionName}`));
            };
        } else {
            return null;
        }
    }

    gotoRunCode(processId, revisionNum, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, '/run-code'), processId, newWindow);
    }

    gotoRunCodeFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum, '/run-code'));
        };
    }

    gotoCreateCode(processId, revisionNum, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, '/create-adi-project'), processId, newWindow);
    }

    gotoCreateCodeFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum, '/create-adi-project'));
        };
    }

    gotoTextEditor(processId, revisionNum, newWindow) {
        this.navigateToPath(processId, this.getPath(processId, revisionNum, '/text'), newWindow);
    }

    gotoTextEditorFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum, '/text'));
        };
    }

    gotoWizardMode(processId, revisionNum, newWindow) {
        this.navigateToPath(this.getPath(processId, revisionNum, '/wizard'), newWindow);
    }

    gotoWizardModeFunc(processId, revisionNum) {
        return () => {
            this.getHistory().push(this.getPath(processId, revisionNum, '/wizard'));
        };
    }

}

export default new NavigationUtils();



// WEBPACK FOOTER //
// ./src/utils/NavigationUtils.js